import React, { createContext, useContext, useState, useEffect } from 'react';
import * as api from '../api/currencyApi';
import { message } from 'antd';

interface CurrencyContextProps {
    currencies: api.Currency[];
    loading: boolean;
    fetchCurrencies: () => Promise<void>;
    createCurrency: (data: api.Currency) => Promise<void>;
    updateCurrency: (id: string, data: api.Currency) => Promise<void>;
    deleteCurrency: (id: string) => Promise<void>;
}

const CurrencyContext = createContext<CurrencyContextProps | undefined>(undefined);

export const CurrencyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [currencies, setCurrencies] = useState<api.Currency[]>([]);
    const [loading, setLoading] = useState(false);

    const fetchCurrencies = async () => {
        setLoading(true);
        try {
            const res = await api.getCurrencies();
            setCurrencies(res.data);
        } catch (err) {
            message.error('Failed to fetch currencies');
        }
        setLoading(false);
    };

    const createCurrencyHandler = async (data: api.Currency) => {
        try {
            await api.createCurrency(data);
            message.success('Currency created');
            fetchCurrencies();
        } catch (err) {
            message.error('Failed to create currency');
        }
    };

    const updateCurrencyHandler = async (id: string, data: api.Currency) => {
        try {
            await api.updateCurrency(id, data);
            message.success('Currency updated');
            fetchCurrencies();
        } catch (err) {
            message.error('Failed to update currency');
        }
    };

    const deleteCurrencyHandler = async (id: string) => {
        try {
            await api.deleteCurrency(id);
            message.success('Currency deleted');
            fetchCurrencies();
        } catch (err) {
            message.error('Failed to delete currency');
        }
    };

    useEffect(() => { fetchCurrencies(); }, []);

    return (
        <CurrencyContext.Provider value={{
            currencies, loading,
            fetchCurrencies,
            createCurrency: createCurrencyHandler,
            updateCurrency: updateCurrencyHandler,
            deleteCurrency: deleteCurrencyHandler
        }}>
            {children}
        </CurrencyContext.Provider>
    );
};

export const useCurrency = () => {
    const ctx = useContext(CurrencyContext);
    if (!ctx) throw new Error('useCurrency must be used within CurrencyProvider');
    return ctx;
};
